import { useEffect, useMemo, useState } from 'react';
import { Megaphone, ExternalLink, CheckCircle2, XCircle, Clock, Hourglass, Pause, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/common/Primitives';
import { Countdown } from '@/components/common/Countdown';
import { PageTitle } from '@/pages/dashboard/DashboardPages';
import { adService, AD_PACKAGES, formatBRL } from '@/services/ads';
import { formatDate } from '@/lib/format';
import { useSeo } from '@/lib/seo';
import { cn } from '@/lib/utils';

const TABS = [
  ['em_analise', 'Comprovante em análise'],
  ['aguardando_pagamento', 'Aguardando pagamento'],
  ['agendada', 'Na fila'],
  ['ativa', 'No ar'],
  ['pausada', 'Pausadas'],
  ['expirada', 'Encerradas'],
  ['recusada', 'Recusadas'],
];

const BADGE = {
  aguardando_pagamento: { text: 'Aguardando pagamento', cls: 'border-amber-300/25 bg-amber-300/10 text-amber-200', icon: Hourglass },
  em_analise: { text: 'Comprovante em análise', cls: 'border-cyan-300/25 bg-cyan-300/10 text-cyan-100', icon: Clock },
  agendada: { text: 'Na fila', cls: 'border-white/10 bg-white/5 text-foreground', icon: Clock },
  ativa: { text: 'No ar', cls: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200', icon: CheckCircle2 },
  pausada: { text: 'Pausada', cls: 'border-amber-300/25 bg-amber-300/10 text-amber-200', icon: Pause },
  expirada: { text: 'Encerrada', cls: 'border-white/10 bg-white/5 text-muted-foreground', icon: CheckCircle2 },
  recusada: { text: 'Recusada', cls: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.1)] text-[rgb(255,150,165)]', icon: XCircle },
};

// O banco só muda o status nas ações; aqui mostramos o estado real pelo horário atual
function effectiveStatus(c) {
  const now = Date.now();
  if (c.status === 'agendada' && c.starts_at && new Date(c.starts_at).getTime() <= now) {
    return new Date(c.ends_at).getTime() <= now ? 'expirada' : 'ativa';
  }
  if (c.status === 'ativa' && c.ends_at && new Date(c.ends_at).getTime() <= now) return 'expirada';
  return c.status;
}

const Stat = ({ label, value }) => (
  <div className="rounded-md border border-white/10 bg-[#0b0f16] px-3 py-2">
    <div className="font-display text-lg font-semibold tabular-nums">{value}</div>
    <div className="text-[11px] text-muted-foreground">{label}</div>
  </div>
);

function Metrics({ id }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    adService.metrics(id).then(setM).catch(() => setM(null));
  }, [id]);
  if (!m) return null;
  return (
    <div className="mt-3 grid grid-cols-3 gap-2" data-testid="admin-ad-metrics">
      <Stat label="Visualizações" value={m.views} />
      <Stat label="Cliques" value={m.clicks} />
      <Stat label="Taxa de clique (CTR)" value={`${m.ctr}%`} />
    </div>
  );
}

function AdCard({ c, busy, onApprove, onReject, onProof, onPause, onResume, onDelete }) {
  const badge = BADGE[c.view] || BADGE.aguardando_pagamento;
  const Icon = badge.icon;
  const pkg = AD_PACKAGES.find((p) => p.days === c.days);
  const showMetrics = c.view === 'ativa' || c.view === 'expirada' || c.view === 'pausada';

  return (
    <article className="surface p-4" data-testid="admin-ad-row">
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <img src={c.image_url} alt="Banner" className="h-20 w-full shrink-0 rounded-md border border-white/10 object-cover md:w-72" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs', badge.cls)}>
              <Icon className="h-3 w-3" /> {badge.text}
            </span>
            <span className="font-medium">{c.companyName}</span>
          </div>
          <div className="text-sm">
            {pkg?.label || `${c.days} dias`} · <span className="font-semibold">{formatBRL(c.price_cents)}</span>
          </div>
          {c.starts_at && (
            <div className="text-[11px] text-muted-foreground">
              Período: {formatDate(c.starts_at)} até {formatDate(c.ends_at)}
            </div>
          )}
          {(c.view === 'ativa' || c.view === 'agendada') && <div><Countdown c={c} /></div>}
          <a href={c.target_url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 break-all text-xs text-primary hover:underline">
            <ExternalLink className="h-3 w-3" /> {c.target_url}
          </a>
          {c.admin_note && <div className="text-xs text-[rgb(255,150,165)]">Motivo: {c.admin_note}</div>}
          {showMetrics && <Metrics id={c.id} />}
        </div>
        <div className="flex flex-wrap gap-2 md:flex-col">
          {c.payment_proof_path && (
            <Button size="sm" variant="secondary" className="border border-white/10" onClick={() => onProof(c)} data-testid="admin-ad-proof">
              <ExternalLink /> Ver comprovante
            </Button>
          )}
          {c.view === 'em_analise' && (
            <>
              <Button size="sm" className="btn-glow" disabled={busy} onClick={() => onApprove(c)} data-testid="admin-ad-approve">
                <CheckCircle2 /> Aprovar
              </Button>
              <Button size="sm" variant="secondary" className="border border-white/10" disabled={busy} onClick={() => onReject(c)} data-testid="admin-ad-reject">
                <XCircle /> Recusar
              </Button>
            </>
          )}
          {c.view === 'aguardando_pagamento' && (
            <Button size="sm" variant="secondary" className="border border-white/10" disabled={busy} onClick={() => onReject(c)}>
              <XCircle /> Cancelar pedido
            </Button>
          )}
          {(c.view === 'ativa' || c.view === 'agendada') && (
            <Button size="sm" variant="secondary" className="border border-white/10" disabled={busy} onClick={() => onPause(c)} data-testid="admin-ad-pause">
              <Pause /> Pausar
            </Button>
          )}
          {c.view === 'pausada' && (
            <Button size="sm" className="btn-glow" disabled={busy} onClick={() => onResume(c)} data-testid="admin-ad-resume">
              <Play /> Retomar
            </Button>
          )}
          {!['em_analise', 'aguardando_pagamento'].includes(c.view) && (
            <Button size="sm" variant="ghost" className="text-[rgb(255,130,150)] hover:bg-[rgba(255,59,92,0.08)]" disabled={busy} onClick={() => onDelete(c)} data-testid="admin-ad-delete">
              <Trash2 /> Excluir
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

export function AdminSponsored() {
  useSeo({ title: 'Administração · Anúncios patrocinados' });
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState('em_analise');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      setRows(await adService.adminList());
    } catch (e) {
      toast.error(e.message);
      setRows([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withView = useMemo(() => (rows || []).map((c) => ({ ...c, view: effectiveStatus(c) })), [rows]);
  const counts = useMemo(
    () => Object.fromEntries(TABS.map(([k]) => [k, withView.filter((c) => c.view === k).length])),
    [withView]
  );
  const approvedTotal = useMemo(
    () => withView.filter((c) => ['agendada', 'ativa', 'expirada', 'pausada'].includes(c.view)).reduce((s, c) => s + c.price_cents, 0),
    [withView]
  );
  const list = withView.filter((c) => c.view === tab);

  const run = async (c, fn, msg) => {
    setBusyId(c.id);
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const approve = (c) => {
    const ok = window.confirm(
      `Confirme que o Pix de ${formatBRL(c.price_cents)} de ${c.companyName} foi recebido na sua conta. Aprovar a campanha?`
    );
    if (!ok) return;
    setBusyId(c.id);
    adService
      .approve(c.id)
      .then((start) => {
        const isNow = new Date(start).getTime() <= Date.now();
        toast.success(isNow ? 'Campanha no ar agora' : `Campanha na fila: começa em ${formatDate(start)}`);
        return load();
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setBusyId(null));
  };

  const reject = (c) => {
    const note = window.prompt('Motivo da recusa (a empresa vai ver esta mensagem):', '');
    if (note === null) return;
    run(c, () => adService.reject(c.id, note.trim()), 'Campanha recusada');
  };

  const pause = (c) => {
    if (!window.confirm('Pausar esta campanha? Ela sai da capa até você retomar.')) return;
    run(c, () => adService.pause(c.id), 'Campanha pausada');
  };

  const resume = (c) => run(c, () => adService.resume(c.id), 'Campanha retomada');

  const remove = (c) => {
    const ok = window.confirm(
      'Excluir esta campanha? Ela sai do ar na hora e não pode ser recuperada.\n\nSe houve pagamento, o reembolso é feito manualmente pelo Pix.'
    );
    if (!ok) return;
    run(c, () => adService.remove(c.id), 'Campanha excluída');
  };

  const openProof = async (c) => {
    try {
      const url = await adService.proofUrl(c.payment_proof_path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-sponsored">
      <PageTitle
        title="Anúncios patrocinados"
        text="Confira o comprovante Pix, aprove, recuse, pause ou exclua. A fila respeita o limite de 10 banners ao mesmo tempo."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Total aprovado" value={formatBRL(approvedTotal)} />
        <Stat label="No ar agora" value={counts.ativa} />
        <Stat label="Na fila" value={counts.agendada} />
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <Button
            key={k}
            size="sm"
            variant={tab === k ? 'default' : 'secondary'}
            className={tab === k ? 'btn-glow' : 'border border-white/10'}
            onClick={() => setTab(k)}
            data-testid={`admin-ads-tab-${k}`}
          >
            {label} <span className="ml-1.5 font-mono text-[11px] opacity-70">{counts[k]}</span>
          </Button>
        ))}
      </div>

      {rows === null ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : list.length ? (
        <div className="space-y-3">
          {list.map((c) => (
            <AdCard
              key={c.id}
              c={c}
              busy={busyId === c.id}
              onApprove={approve}
              onReject={reject}
              onProof={openProof}
              onPause={pause}
              onResume={resume}
              onDelete={remove}
            />
          ))}
        </div>
      ) : (
        <EmptyState icon={Megaphone} title="Nada nesta lista" text="Nenhuma campanha com este status." testId="admin-ads-empty" />
      )}
    </div>
  );
}